import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Component, NgModule, InjectionToken, APP_INITIALIZER, Injector } from '@angular/core';
import * as i2 from '@angular/router';
import { RouterModule } from '@angular/router';
import { map, switchMap, tap, catchError, finalize, take } from 'rxjs/operators';
import { of, BehaviorSubject, from, throwError } from 'rxjs';
import * as i1 from 'oidc-client';
import { UserManager } from 'oidc-client';
import * as i2$1 from '@angular/common/http';
import { HTTP_INTERCEPTORS, HttpHeaders } from '@angular/common/http';

/**
 * The purpose of this class is to provide a way for a consumer of this library to hook on to the events
 * that occur in this library to do some processing before/after actions occur.
 */
class XpoAuthEventHandler {
}
class XpoAuthDefaultEventHandler extends XpoAuthEventHandler {
    preSignIn() {
        return of(null);
    }
    postSignIn(user) {
        return of(null);
    }
    preSignOut(user) {
        return of(null);
    }
    postSignOut() {
        return of(null);
    }
    preRefresh(user) {
        return of(null);
    }
    postRefresh(user) {
        return of(null);
    }
}
const DefaultAuthEventHandlerProvider = {
    provide: XpoAuthEventHandler,
    useClass: XpoAuthDefaultEventHandler,
};

class XpoAuthenticationService {
    constructor(manager, router, authEventListener) {
        this.manager = manager;
        this.router = router;
        this.authEventListener = authEventListener;
        this.userSource$ = new BehaviorSubject(null);
        this.manager.events.addAccessTokenExpired((event) => {
            // TODO: will this be handled with the 403 error interceptor?
        });
        // Listening in to when the user is added to store the value
        this.manager.events.addUserLoaded((user) => {
            // Add Hook here
            this.setUser(user);
        });
        this.manager.events.addSilentRenewError(() => {
            // TODO: forcefully sign out user? or a snackbar?
        });
        this.manager.events.addUserSessionChanged(() => {
            // TODO
        });
    }
    /**
     * Return current user.
     */
    getUser() {
        return this.userSource$.value;
    }
    /**
     * Change stream of current user.
     */
    getUser$() {
        return this.userSource$.asObservable();
    }
    /**
     * Sets value of current user.
     */
    setUser(u) {
        this.userSource$.next(u);
    }
    /**
     * Whether or not the user is logged in.
     */
    isLoggedIn() {
        const user = this.getUser();
        return user != null && !user.expired;
    }
    /**
     * Change stream of whether or no the user is logged in.
     */
    isLoggedIn$() {
        return this.getUser$().pipe(map(() => this.isLoggedIn()));
    }
    /**
     * Returns authorization header that includes the user's access token.
     */
    getAuthorizationHeaderValue() {
        const user = this.getUser();
        return `${user.token_type} ${user.access_token}`;
    }
    /**
     * Triggers the process of signing in the user and redirects to the identity provider.
     * When redirected back to the app, completeSignIn$() should be called
     */
    startSignIn$(redirectUrl) {
        this.redirectUrl = redirectUrl || '/';
        return this.authEventListener.preSignIn().pipe(switchMap(() => from(this.manager.signinRedirect())));
    }
    /**
     * Completes the process of signing in and redirects the user to the url that was stored when
     * startSignIn$() was triggered.
     */
    completeSignIn$() {
        return from(this.manager.signinRedirectCallback()).pipe(switchMap((user) => this.authEventListener.postSignIn(user)), tap(() => {
            this.router.navigateByUrl(this.redirectUrl || '/');
        }), catchError((val) => {
            console.error(`Error authenticating, Redirecting to '${this.redirectUrl || '/'}'`, val);
            this.router.navigateByUrl(this.redirectUrl || '/');
            return of(val);
        }));
    }
    /**
     * Signs out user and redirects them to a signout page.
     */
    signOut$() {
        return this.authEventListener
            .preSignOut(this.getUser())
            .pipe(switchMap(() => from(this.manager.signoutRedirect())));
    }
    /**
     * Completes silent refresh of user's credentials
     */
    completeSilentRefresh$() {
        return from(this.manager.signinSilentCallback());
    }
    /**
     * Starts silent refresh of user's credentials
     */
    startSilentRefresh$() {
        return this.authEventListener.preRefresh(this.getUser()).pipe(switchMap(() => from(this.manager.signinSilent())), finalize(() => this.authEventListener.postRefresh(this.getUser())));
    }
    get redirectUrl() {
        return sessionStorage.getItem(XpoAuthenticationService.AuthRedirectSessionKey);
    }
    set redirectUrl(v) {
        sessionStorage.setItem(XpoAuthenticationService.AuthRedirectSessionKey, v);
    }
}
XpoAuthenticationService.AuthRedirectSessionKey = 'xpo-auth-redirect-key';
XpoAuthenticationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationService, deps: [{ token: i1.UserManager }, { token: i2.Router }, { token: XpoAuthEventHandler }], target: i0.ɵɵFactoryTarget.Injectable });
XpoAuthenticationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.UserManager }, { type: i2.Router }, { type: XpoAuthEventHandler }]; } });

class XpoAuthenticationSilentComponent {
    constructor(authenticationService) {
        this.authenticationService = authenticationService;
    }
    ngOnInit() {
        this.authenticationService
            .completeSilentRefresh$()
            .pipe(take(1))
            .subscribe();
    }
}
XpoAuthenticationSilentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationSilentComponent, deps: [{ token: XpoAuthenticationService }], target: i0.ɵɵFactoryTarget.Component });
XpoAuthenticationSilentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.0.2", type: XpoAuthenticationSilentComponent, selector: "xpo-authentication-silent", ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationSilentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'xpo-authentication-silent',
                    template: '',
                }]
        }], ctorParameters: function () { return [{ type: XpoAuthenticationService }]; } });

/**
 * When navigating to this component, it will check if the user is login in,
 * if so, it will redirect the user to the root of the app, if not, then it will start the
 * login process
 */
class XpoAuthenticationComponent {
    constructor(authenticationService, router) {
        this.authenticationService = authenticationService;
        this.router = router;
    }
    ngOnInit() {
        if (this.authenticationService.isLoggedIn()) {
            this.router.navigate(['/']);
        }
        else {
            this.authenticationService
                .completeSignIn$()
                .pipe(take(1))
                .subscribe();
        }
    }
}
XpoAuthenticationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationComponent, deps: [{ token: XpoAuthenticationService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
XpoAuthenticationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.0.2", type: XpoAuthenticationComponent, selector: "xpo-authentication", ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'xpo-authentication',
                    template: '',
                }]
        }], ctorParameters: function () { return [{ type: XpoAuthenticationService }, { type: i2.Router }]; } });

class XpoPostSignOutComponent {
    constructor(authEventListener) {
        this.authEventListener = authEventListener;
    }
    ngOnInit() {
        this.authEventListener.postSignOut();
    }
}
XpoPostSignOutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoPostSignOutComponent, deps: [{ token: XpoAuthEventHandler }], target: i0.ɵɵFactoryTarget.Component });
XpoPostSignOutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.0.2", type: XpoPostSignOutComponent, selector: "xpo-post-sign-out", ngImport: i0, template: `
    <h3>You have been signed out. <a [routerLink]="'/'">Return To Application</a></h3>
  `, isInline: true, directives: [{ type: i2.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["routerLink", "target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoPostSignOutComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'xpo-post-sign-out',
                    template: `
    <h3>You have been signed out. <a [routerLink]="'/'">Return To Application</a></h3>
  `,
                }]
        }], ctorParameters: function () { return [{ type: XpoAuthEventHandler }]; } });

/**
 * Routes for the components in this library
 *
 * These routes are also declared in the XpoAuth platform so these cannot be changed
 * unless the backend changes it as well
 */
var XpoAuthRoutePaths;
(function (XpoAuthRoutePaths) {
    XpoAuthRoutePaths["Authentication"] = "auth";
    XpoAuthRoutePaths["PostSignOut"] = "postsignout";
    XpoAuthRoutePaths["AuthSilent"] = "authsilent";
})(XpoAuthRoutePaths || (XpoAuthRoutePaths = {}));

const routes = [
    { path: XpoAuthRoutePaths.Authentication, component: XpoAuthenticationComponent },
    { path: XpoAuthRoutePaths.AuthSilent, component: XpoAuthenticationSilentComponent },
    { path: XpoAuthRoutePaths.PostSignOut, component: XpoPostSignOutComponent },
];
class XpoAuthRoutingModule {
}
XpoAuthRoutingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthRoutingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
XpoAuthRoutingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthRoutingModule, imports: [CommonModule, i2.RouterModule], exports: [RouterModule] });
XpoAuthRoutingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthRoutingModule, imports: [[CommonModule, RouterModule.forChild(routes)], RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthRoutingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RouterModule.forChild(routes)],
                    exports: [RouterModule],
                }]
        }] });

const XPO_AUTH_HEADER_KEY = 'Authorization';
const XPO_AUTH_RETRY_ATTEMPTS_KEY = 'XpoAuthRetryAttempts';
/**
 * Intercepts http requests to attach a Authorization token to the header
 */
class XpoAuthenticationInterceptor {
    constructor(injector) {
        this.injector = injector;
        this.maxAttempts = 3;
    }
    intercept(req, next) {
        // We are assuming all calls to a url ending with `.json` do not need our token
        // This was put here to solve an issue where the app-settings.json file was getting intercepted
        // before this service is created. Hence the reason we are injecting the injector instead of the
        // XpoAuthenticationService, this function is being called before that service is created.
        if (!req.url.includes('.json') && (!req.headers || !req.headers.get(XPO_AUTH_HEADER_KEY))) {
            this.authenticationService = this.injector.get(XpoAuthenticationService);
            if (this.authenticationService.isLoggedIn()) {
                // add JWT authentication token to headers
                const authRequest = this.addAuthToHeaders(req);
                return next.handle(authRequest).pipe(catchError((error) => {
                    var _a;
                    // the request failed.  If it is an authentication issue, then we want to try to
                    // update our auth token
                    let attempts = (_a = +sessionStorage.getItem(XPO_AUTH_RETRY_ATTEMPTS_KEY)) !== null && _a !== void 0 ? _a : 0;
                    if (this.isUnauthorizedResponse(error) && attempts <= this.maxAttempts) {
                        sessionStorage.setItem(XPO_AUTH_RETRY_ATTEMPTS_KEY, `${++attempts}`);
                        return this.reauthenticate().pipe(switchMap((user) => {
                            // authenticated. try running the request again. if it fails this time, then we are done
                            const newAuthRequest = this.addAuthToHeaders(req);
                            return next.handle(newAuthRequest);
                        }));
                    }
                    else {
                        sessionStorage.removeItem(XPO_AUTH_RETRY_ATTEMPTS_KEY);
                        // error not due to authentication, so just return it
                        return throwError(error);
                    }
                }));
            }
        }
        // request does not need authentication token
        return next.handle(req);
    }
    addAuthToHeaders(req) {
        return req.clone({
            headers: req.headers.set(XPO_AUTH_HEADER_KEY, this.getAuthHeaderValue()),
        });
    }
    getAuthHeaderValue() {
        return `${this.authenticationService.getAuthorizationHeaderValue()}`;
    }
    isUnauthorizedResponse(res) {
        return res.status === 401;
    }
    // Attempt to reauthenticate
    reauthenticate() {
        // First, try to silently refresh the token
        return this.authenticationService.startSilentRefresh$();
        // NOTE: If we want, we can catch errors from the silentRefresh and force the user
        // through a full signIn process.
        // .pipe(
        //   catchError((error) => {
        //     // Failed to silently refresh, so force the user to go through the entire log-in process
        //     console.error(`XpoAuthenticationInterceptor: SilentRefresh failed. Forcing full sign-in: `, error);
        //     if (!this.authenticationService) {
        //       this.authenticationService = this.injector.get(XpoAuthenticationService);
        //     }
        //     return this.authenticationService.startSignIn$();
        //   })
        // );
    }
}
XpoAuthenticationInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationInterceptor, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
XpoAuthenticationInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

/**
 * Guard that prevents user from access route if they are not authenticated.
 * If not authenticated, will start the authentication process and login the user.
 */
class XpoAuthenticationGuard {
    constructor(authenticationService) {
        this.authenticationService = authenticationService;
    }
    canActivate(route, state) {
        return this.isAuthenticated(state.url);
    }
    canActivateChild(childRoute, state) {
        return this.isAuthenticated(state.url);
    }
    canLoad(route, segments) {
        return this.isAuthenticated(route.path); // TODO: maybe it should be path from root?
    }
    isAuthenticated(returnRoute) {
        if (this.authenticationService.isLoggedIn()) {
            return true;
        }
        this.authenticationService
            .startSignIn$(returnRoute)
            .pipe(take(1))
            .subscribe();
        return false;
    }
}
XpoAuthenticationGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationGuard, deps: [{ token: XpoAuthenticationService }], target: i0.ɵɵFactoryTarget.Injectable });
XpoAuthenticationGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthenticationGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: XpoAuthenticationService }]; } });

class XpoAuthorizationGuard {
    canActivate(route, state) {
        // TODO
        return true;
    }
    canActivateChild(childRoute, state) {
        // TODO
        return true;
    }
    canLoad(route, segments) {
        // TODO
        return true;
    }
}
XpoAuthorizationGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthorizationGuard, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
XpoAuthorizationGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthorizationGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthorizationGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const XPO_AUTH_CONFIG = new InjectionToken('xpoAuthConfig');

class XpoAuthConfig {
    constructor(config) {
        if (!config) {
            throw new Error('Must Provide XpoAuthConfigData');
        }
        if (!config.appCode) {
            throw new Error('Must Provide appCode in XpoAuthConfigData');
        }
        if (!config.scopes) {
        }
        this.appCode = config.appCode;
        this.authFlow = config.authFlow || 'implicit';
        this.responseTypes =
            this.authFlow === 'codePkce' ? XpoAuthConfig.CodePKCEFlowResponseTypes : XpoAuthConfig.ImplicitFlowResponseTypes;
        if (this.authFlow === 'codePkce' && !config.clientSecret) {
            throw new Error('Must Provide a clientSecret in your XpoAuthConfigData when setting authFlow to codePkce');
        }
        this.clientSecret = config.clientSecret;
        this.isProd = !!config.isProd;
        // Appending base scopes with entered ones, and ensuring they are distinct
        this.scopes = Array.from(new Set([...config.scopes, ...XpoAuthConfig.BaseScopes]));
        // If no applicationRootUri, default it to the origin of the app.
        this.applicationRootUri = config.applicationRootUri || window.location.origin;
        // Add the trailing slash if not already there
        this.applicationRootUri = this.applicationRootUri.endsWith('/')
            ? this.applicationRootUri
            : `${this.applicationRootUri}/`;
        // If no authApiUri provided, default it to the authXPO's sandbox or prod environment based the isProd flag
        this.authApiUri = config.authApiUri || (this.isProd ? 'https://id-api.xpo.com' : 'https://id-api-sandbox.xpo.com');
        // If no authorityUri,default it to the authXPO's sandbox or prod environment based the isProd flag
        this.authorityUri = config.authorityUri || (this.isProd ? 'https://id.xpo.com' : 'https://id-sandbox.xpo.com');
        this.switchApiUri =
            config.switchApiUri || (this.isProd ? 'https://switch.xpoapi.com' : 'https://switch-sandbox.xpoapi.com');
        this.unauthorizedPath = config.unauthorizedPath || '/unauthorized';
    }
}
XpoAuthConfig.BaseScopes = ['openid', 'email', 'name', 'xpo-auth-api'];
XpoAuthConfig.ImplicitFlowResponseTypes = ['id_token', 'token'];
XpoAuthConfig.CodePKCEFlowResponseTypes = ['code'];

class XpoAuthSwitchApiConfig {
}

function userManagerFactory(authConfig) {
    return new UserManager({
        authority: authConfig.authorityUri,
        client_id: authConfig.appCode,
        post_logout_redirect_uri: `${authConfig.applicationRootUri}${XpoAuthRoutePaths.PostSignOut}`,
        redirect_uri: `${authConfig.applicationRootUri}${XpoAuthRoutePaths.Authentication}`,
        response_type: authConfig.responseTypes.join(' '),
        scope: authConfig.scopes.join(' '),
        silent_redirect_uri: `${authConfig.applicationRootUri}${XpoAuthRoutePaths.AuthSilent}`,
        automaticSilentRenew: true,
        filterProtocolClaims: true,
        loadUserInfo: true,
        client_secret: authConfig.clientSecret,
    });
}
class XpoAuthAppInitializeLogInDependency {
}
function switchApiConfigFactory(authConfig) {
    return { apiUri: authConfig.switchApiUri };
}
function loginOnInitApp(logInDependency, injector) {
    const result = () => new Promise((resolve, reject) => {
        // Once dependency triggers that its ready, then start login.
        logInDependency
            .ready$()
            .pipe(take(1), switchMap(() => {
            // We lazy resolve the XpoAuthenticationService here because only at this point
            // the app-settings config parameters are loaded and we can proceed with auth
            const authenticationService = injector.get(XpoAuthenticationService);
            // Check the current url see if just got back from auth redirect
            // and let the initialization process continue so the auth-component will complete the sign in
            if (window.location.pathname.includes(`/${XpoAuthRoutePaths.Authentication}`) ||
                window.location.pathname.includes(`/${XpoAuthRoutePaths.AuthSilent}`) ||
                window.location.pathname.includes(`/${XpoAuthRoutePaths.PostSignOut}`) ||
                authenticationService.isLoggedIn()) {
                return of(true);
            }
            return authenticationService.startSignIn$().pipe(take(1));
        }))
            .subscribe({ next: (res) => resolve(res), error: () => reject('Unable to finalize auth in APP_INIT.') });
    });
    return result;
}
class XpoAuthModule {
    static forRoot(authModuleConfig, deprecatedAuthEventListener) {
        const castedAuthModuleConfig = authModuleConfig;
        if (!castedAuthModuleConfig.authConfigProvider) {
            console.warn(`WARN XpoAuthModule:
        The use of passing multiple arguments to the .forRoot method of the XpoAuthModule is deprecated.
        Please use the "XpoAuthModuleConfig" interface and pass in your config values in that object.
        Example:
        From:
        XpoAuthModule.forRoot(
          {
            provide: XPO_AUTH_CONFIG,
            useFactory: authConfigFactory,
            deps: [XpoConfigService],
          },
        ),
        To:
        XpoAuthModule.forRoot(<XpoAuthModuleConfig>{
          authConfigProvider:{
            provide: XPO_AUTH_CONFIG,
            useFactory: authConfigFactory,
            deps: [XpoConfigService],
          },
        }),
      `);
        }
        const authConfigProviderValue = castedAuthModuleConfig.authConfigProvider || authModuleConfig;
        const authEventListenerValue = castedAuthModuleConfig.authEventListener || deprecatedAuthEventListener || DefaultAuthEventHandlerProvider;
        const providers = [authConfigProviderValue, authEventListenerValue];
        if (castedAuthModuleConfig.authAppInitializeLogInDependency) {
            providers.push(
            // Provide the user's dependency
            castedAuthModuleConfig.authAppInitializeLogInDependency, 
            // Casting it to a known type to this module
            {
                provide: XpoAuthAppInitializeLogInDependency,
                useExisting: castedAuthModuleConfig.authAppInitializeLogInDependency,
            }, 
            // Used that known type to start login
            {
                provide: APP_INITIALIZER,
                useFactory: loginOnInitApp,
                deps: [XpoAuthAppInitializeLogInDependency, Injector],
                multi: true,
            });
        }
        return { ngModule: XpoAuthModule, providers };
    }
}
XpoAuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
XpoAuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthModule, declarations: [XpoAuthenticationComponent, XpoAuthenticationSilentComponent, XpoPostSignOutComponent], imports: [CommonModule,
        XpoAuthRoutingModule] });
XpoAuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthModule, providers: [
        { provide: UserManager, useFactory: userManagerFactory, deps: [XPO_AUTH_CONFIG] },
        { provide: XpoAuthSwitchApiConfig, useFactory: switchApiConfigFactory, deps: [XPO_AUTH_CONFIG] },
        XpoAuthenticationGuard,
        XpoAuthorizationGuard,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: XpoAuthenticationInterceptor,
            multi: true,
        },
    ], imports: [[
            CommonModule,
            XpoAuthRoutingModule, // Keep last
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        XpoAuthRoutingModule, // Keep last
                    ],
                    declarations: [XpoAuthenticationComponent, XpoAuthenticationSilentComponent, XpoPostSignOutComponent],
                    providers: [
                        { provide: UserManager, useFactory: userManagerFactory, deps: [XPO_AUTH_CONFIG] },
                        { provide: XpoAuthSwitchApiConfig, useFactory: switchApiConfigFactory, deps: [XPO_AUTH_CONFIG] },
                        XpoAuthenticationGuard,
                        XpoAuthorizationGuard,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: XpoAuthenticationInterceptor,
                            multi: true,
                        },
                    ],
                }]
        }] });

class XpoAuthorizationService {
}
XpoAuthorizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthorizationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
XpoAuthorizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthorizationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthorizationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class XpoAuthSwitchApiService {
    constructor(authSwitchApiConfig, http) {
        this.authSwitchApiConfig = authSwitchApiConfig;
        this.http = http;
        this.defaultHeaders = new HttpHeaders();
        this.defaultHeaders = new HttpHeaders();
        this.defaultHeaders.set('Accept', 'application/json');
        this.baseUriUserApplications = `${this.authSwitchApiConfig.apiUri}/userapplications`;
    }
    /**
     * @example
     *
     * getUserImageSrc(user)
     * // or
     * getUserImageSrc(user, 'http://picasaweb.google.com/data/entry/api/user/<hereYourUserIdOrYourEmail>?alt=json')
     *
     * @param user  The auth user object
     * @param uriOverride uri to override to get another profile picture
     * @returns The user's profile picture
     */
    getUserImageSrc(user, uriOverride) {
        return uriOverride || `${this.authSwitchApiConfig.apiUri}/users/${user.profile.upn}/picture`;
    }
    /**
     * getUserDetails(user)
     *
     * @param user  The auth user object
     * @returns The user's details object
     */
    getUserDetails(user) {
        const uri = `${this.authSwitchApiConfig.apiUri}/users/${user.profile.upn}/profile`;
        return this.http.get(uri, { headers: this.defaultHeaders });
    }
    /**
     * @example
     * getAllUserApplications(user)
     *
     * @param user  The auth user object
     * @param force  if true will force to rebuild the applications list
     * @returns The list of applications either forced get or cached
     */
    getAllUserApplications(user, force = false) {
        let uri = `${this.baseUriUserApplications}/${user.profile.upn}`;
        if (force) {
            uri = `${uri}/true`;
        }
        return this.http.get(uri, { headers: this.defaultHeaders });
    }
    /**
     * @example
     * getRecentUserApplications(user)
     *
     * @param user  The auth user object
     * @returns The list of recent applications
     */
    getRecentUserApplications(user) {
        const uri = `${this.baseUriUserApplications}/${user.profile.upn}/recent`;
        return this.http.get(uri, { headers: this.defaultHeaders });
    }
    /**
     * @example
     * getBaseUriUserApplications()
     *
     * @returns The base Uri of the userapplications route
     */
    getBaseUriUserApplications() {
        return `${this.baseUriUserApplications}`;
    }
}
XpoAuthSwitchApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthSwitchApiService, deps: [{ token: XpoAuthSwitchApiConfig }, { token: i2$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
XpoAuthSwitchApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthSwitchApiService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.2", ngImport: i0, type: XpoAuthSwitchApiService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: XpoAuthSwitchApiConfig }, { type: i2$1.HttpClient }]; } });

/*
 * Public API Surface of ngx-auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DefaultAuthEventHandlerProvider, XPO_AUTH_CONFIG, XPO_AUTH_HEADER_KEY, XPO_AUTH_RETRY_ATTEMPTS_KEY, XpoAuthAppInitializeLogInDependency, XpoAuthConfig, XpoAuthDefaultEventHandler, XpoAuthEventHandler, XpoAuthModule, XpoAuthRoutePaths, XpoAuthRoutingModule, XpoAuthSwitchApiConfig, XpoAuthSwitchApiService, XpoAuthenticationComponent, XpoAuthenticationGuard, XpoAuthenticationInterceptor, XpoAuthenticationService, XpoAuthenticationSilentComponent, XpoAuthorizationGuard, XpoAuthorizationService, XpoPostSignOutComponent, loginOnInitApp, switchApiConfigFactory, userManagerFactory };
